import React, { useState } from 'react';
import { MapPin, Navigation, TriangleAlert as AlertTriangle, Loader } from 'lucide-react';
import { getCurrentLocationDetails, storeCoords, getLocationPermissionState, setLocationPermissionState } from '../Utils/location';
import './LocationPermissionModal.css';

interface LocationPermissionModalProps {
  onGranted: () => void;
  onManualCity: (city: string) => void;
}

type Phase = 'ask' | 'detecting' | 'browser_blocked' | 'manual_city';

const LocationPermissionModal: React.FC<LocationPermissionModalProps> = ({ onGranted, onManualCity }) => {
  const [phase, setPhase] = useState<Phase>('ask');
  const [cityInput, setCityInput] = useState('');
  const [cityError, setCityError] = useState('');
  const [retrying, setRetrying] = useState(false);

  const requestGPS = async () => {
    setPhase('detecting');
    setRetrying(false);
    try {
      const result = await getCurrentLocationDetails();
      if ('type' in result) {
        if (result.type === 'permission_denied') {
          setLocationPermissionState('denied_browser');
          setPhase('browser_blocked');
        } else {
          setLocationPermissionState('pending');
          setPhase('ask');
        }
        return;
      }
      storeCoords(result.latitude, result.longitude);
      localStorage.setItem('activeCity', result.city.trim());
      window.dispatchEvent(new Event('activeCityChanged'));
      setLocationPermissionState('granted');
      onGranted();
    } catch {
      setLocationPermissionState('pending');
      setPhase('ask');
    }
  };

  const handleEnableLocation = () => {
    requestGPS();
  };

  const handleRetry = async () => {
    setRetrying(true);
    await requestGPS();
    setRetrying(false);
  };

  const handleManualCitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = cityInput.trim();
    if (!trimmed) {
      setCityError('Please enter your city name.');
      return;
    }
    localStorage.setItem('activeCity', trimmed);
    window.dispatchEvent(new Event('activeCityChanged'));
    setLocationPermissionState('manual');
    onManualCity(trimmed);
  };

  const getBrowserInstructions = (): string => {
    const ua = navigator.userAgent;
    if (/Firefox/.test(ua)) {
      return 'Click the lock icon in the address bar → More Information → Permissions → Access your location → Allow.';
    }
    if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
      return 'Go to Safari menu → Settings for This Website → Location → Allow.';
    }
    return 'Click the lock icon in the address bar → Site settings → Location → Allow.';
  };

  return (
    <div className="lpm-overlay">
      <div className="lpm-card">

        {phase === 'ask' && (
          <>
            <div className="lpm-icon-ring">
              <MapPin size={32} className="lpm-icon" />
            </div>
            <h2 className="lpm-title">We need your location</h2>
            <p className="lpm-desc">
              To show you nearby kitchens, auto-fill your delivery address, and help delivery partners reach you accurately, HomeCookt needs access to your location.
            </p>
            <button className="lpm-btn lpm-btn--primary" onClick={handleEnableLocation}>
              <Navigation size={18} />
              Enable Location
            </button>
            <button className="lpm-btn lpm-btn--ghost" onClick={() => setPhase('manual_city')}>
              Enter city manually instead
            </button>
          </>
        )}

        {phase === 'detecting' && (
          <>
            <div className="lpm-icon-ring lpm-icon-ring--loading">
              <Loader size={32} className="lpm-icon lpm-spin" />
            </div>
            <h2 className="lpm-title">Detecting your location...</h2>
            <p className="lpm-desc">Please allow location access in the browser prompt that appears.</p>
          </>
        )}

        {phase === 'browser_blocked' && (
          <>
            <div className="lpm-icon-ring lpm-icon-ring--warning">
              <AlertTriangle size={32} className="lpm-icon lpm-icon--warning" />
            </div>
            <h2 className="lpm-title">Location is blocked</h2>
            <p className="lpm-desc">
              Your browser has blocked location access. To enable it:
            </p>
            <div className="lpm-instructions">
              <p>{getBrowserInstructions()}</p>
            </div>
            <button className="lpm-btn lpm-btn--primary" onClick={handleRetry} disabled={retrying}>
              {retrying ? <><Loader size={16} className="lpm-spin" /> Checking...</> : <><Navigation size={16} /> I've enabled it, try again</>}
            </button>
            <button className="lpm-btn lpm-btn--ghost" onClick={() => setPhase('manual_city')}>
              Enter city manually instead
            </button>
          </>
        )}

        {phase === 'manual_city' && (
          <>
            <div className="lpm-icon-ring">
              <MapPin size={32} className="lpm-icon" />
            </div>
            <h2 className="lpm-title">Enter your city</h2>
            <p className="lpm-desc">
              We'll use this to show nearby kitchens. You can still enable location access later from your browser settings.
            </p>
            <form className="lpm-city-form" onSubmit={handleManualCitySubmit}>
              <input
                className="lpm-city-input"
                type="text"
                placeholder="e.g. Mumbai, Chennai, Bangalore..."
                value={cityInput}
                onChange={e => { setCityInput(e.target.value); setCityError(''); }}
                autoFocus
              />
              {cityError && <p className="lpm-city-error">{cityError}</p>}
              <button type="submit" className="lpm-btn lpm-btn--primary">
                <MapPin size={16} />
                Confirm City
              </button>
            </form>
            <button className="lpm-btn lpm-btn--ghost" onClick={() => setPhase(getLocationPermissionState() === 'denied_browser' ? 'browser_blocked' : 'ask')}>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationPermissionModal;
